import { SkyButton, SkyText } from "@skypointcloud/skypoint-ui";
import { useLocation } from "wouter";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { usePermissions } from "@/hooks/use-permissions";
import { FullPageLoader } from "@/components/shell/FullPageLoader";
import { tenantGap, useTenants } from "@/hooks/use-tenants";
import { InstancesIcon, TenantsIcon } from "@/lib/icons";
import { NEW_TENANT_ADDRESS, ROUTES } from "@/lib/routes";

/**
 * Overview with no instance to show (Session 10, B1 admin landing).
 *
 * A signed-in user on `/` or `/overview` with no instance selected lands in the first instance
 * they can read (`landingInstance`). When there is none — a fresh deployment, or an account
 * nobody has shared an instance with yet — this is what Overview says. A role that can create an
 * instance gets the way to do it; everyone else is told who can.
 *
 * Rendered by `App.tsx` outside the shell, like the picker: the shell's routes are instance
 * routes, and there is no instance.
 */
/**
 * B-54 (round 9): a person granted on a tenant or an instance whose instances are all disabled
 * (or not created yet) sees none in `/me`, so "nothing has been shared with you" was false. Their
 * grant still stands, so the sentence says the instances are switched off and who can turn one on.
 */
export const INSTANCES_SWITCHED_OFF =
  "Overview shows one instance's connections, schedules, imports and tests. You have access, but every instance it covers is disabled or not created yet. Contact Skypoint support to enable one.";

/**
 * #921: on a deployment with no tenant yet, a PlatformAdmin is sent to make the tenant first (its
 * set-up creates its first instance too), never to an instance dialog with an empty tenant list.
 * Owner 2026-10-13: a welcome, not an absence.
 */
export const WELCOME_HEADING = "Welcome to skyData";
export const NO_TENANT_YET =
  "skyData brings data from the systems your business runs on into your own Snowflake, on the schedule you choose. Start by creating your first tenant, the organization whose data skyData will manage. Its first instances are created with it. Then connect a source and choose where the data lands.";

/**
 * #921 review: tenants exist, but every one is disabled, so no instance can be opened or made.
 * Owner 2026-10-13: said as a pause the person can undo.
 */
export const PAUSED_HEADING = "Your tenants are paused";
export const NO_ACTIVE_TENANT =
  "Every tenant is disabled right now, so there is nothing to show yet. Enable a tenant on the Tenants page to pick up where you left off, or create a new one.";

type TenantGap = ReturnType<typeof tenantGap> | null;

/** The heading for the state: a welcome, a pause, or the plain "No instance yet". */
function headingFor(gap: TenantGap): string {
  if (gap === "none") return WELCOME_HEADING;
  if (gap === "disabled") return PAUSED_HEADING;
  return "No instance yet";
}

/** The sentence under the heading, by the same order of checks as the heading. */
function bodyFor(gap: TenantGap, canCreate: boolean, grantedButSwitchedOff: boolean): string {
  if (gap === "none") return NO_TENANT_YET;
  if (gap === "disabled") return NO_ACTIVE_TENANT;
  if (canCreate) {
    return "Overview shows one instance's connections, schedules, imports and tests. Create the first instance to start.";
  }
  if (grantedButSwitchedOff) return INSTANCES_SWITCHED_OFF;
  return "Overview shows one instance's connections, schedules, imports and tests. No instance has been shared with you yet. Ask an administrator for access.";
}

/** The one button the state offers, or nothing for a role that cannot act. */
function NoInstanceAction({ gap, canCreate }: { gap: TenantGap; canCreate: boolean }) {
  const [, navigate] = useLocation();
  if (gap === "none") {
    return (
      <div>
        <SkyButton
          appearance="primary"
          icon={TenantsIcon}
          onClick={() => navigate(NEW_TENANT_ADDRESS)}
          testId="overview-no-tenant-create"
        >
          Create your first tenant
        </SkyButton>
      </div>
    );
  }
  if (gap === "disabled") {
    return (
      <div>
        <SkyButton
          appearance="primary"
          icon={TenantsIcon}
          onClick={() => navigate(ROUTES.adminTenants)}
          testId="overview-open-tenants"
        >
          Open Tenants
        </SkyButton>
      </div>
    );
  }
  if (!canCreate) return null;
  return (
    <div>
      <SkyButton
        appearance="primary"
        icon={InstancesIcon}
        onClick={() => navigate(ROUTES.adminInstances)}
        testId="overview-no-instance-create"
      >
        Open Instances
      </SkyButton>
    </div>
  );
}

export function OverviewNoInstance() {
  const { canAny, can } = usePermissions();
  const { me } = useTenantInstance();
  const canCreate = canAny("platform.instances.manage", "tenant.instances.manage");
  const canCreateTenant = can("platform.tenants.manage");
  // Only a tenant manager asks: the list is platform scope, and nobody else can act on its answer.
  const tenants = useTenants({ enabled: canCreateTenant });
  // The same rule as Instances and the New instance dialog (`tenantGap`).
  const gap = canCreateTenant && tenants.data !== undefined ? tenantGap(tenants.data) : null;
  const grantedButSwitchedOff = (me?.assignments ?? []).some(
    (assignment) => assignment.scopeType === "tenant" || assignment.scopeType === "instance",
  );

  // The heading waits for the tenant list: "No instance yet" first and the welcome a moment
  // later would send the person the wrong way.
  if (canCreateTenant && tenants.isPending) {
    return <FullPageLoader message="Loading…" />;
  }

  return (
    <main
      id="main-content"
      className="mx-auto flex max-w-2xl flex-col gap-3 p-6"
      tabIndex={-1}
      data-testid="overview-no-instance"
    >
      <SkyText as="h1" size={600} weight="semibold" block>
        {headingFor(gap)}
      </SkyText>
      <SkyText as="p" size={300} color="muted" block>
        {bodyFor(gap, canCreate, grantedButSwitchedOff)}
      </SkyText>
      <NoInstanceAction gap={gap} canCreate={canCreate} />
    </main>
  );
}
